import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import SecureStorage from '../utils/secureStorage';
import './AdminDashboard.css';
import { LineChart, DonutChart } from './DashboardCharts';

const LOCAL_API_BASE_URL = "http://localhost:5000";
const RENDER_API_BASE_URL = "https://wecareehs-backend.onrender.com";
const API_BASE_URL = process.env.REACT_APP_USE_RENDER === "true" ? RENDER_API_BASE_URL : LOCAL_API_BASE_URL;

const TABS = { OVERVIEW: 'overview', USERS: 'users', FEEDBACK: 'feedback', SECURITY: 'security' };

const AdminDashboard = () => {
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const navigate = useNavigate();
  const { isAdminAuthenticated, logout } = useContext(AuthContext);

  const [chartData, setChartData] = useState({ growth: [], feedbackType: [] });

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    let intervalId;

    const fetchData = async () => {
      // Double-check: verify role from JWT, not just isAdminAuthenticated state
      const role = SecureStorage.getRole(true);
      if (!isAdminAuthenticated || role !== 'admin') {
        navigate('/admin/login');
        return;
      }

      try {
        const adminToken = SecureStorage.getToken(true);
        if (!adminToken) {
          navigate('/admin/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            logout();
            navigate('/admin/login');
            return;
          }
          throw new Error(data.error || 'Failed to load data');
        }

        const users = data.userRegistrations || [];
        const feeds = data.feedbacks || [];
        const logs = data.securityLogs || [];

        // SAFETY CHECK: ensure no password hashes are displayed
        const safeUsers = users.map(({ password, ...rest }) => rest);

        setUserRegistrations(safeUsers);
        setFeedbacks(feeds);
        setSecurityLogs(logs);

        // Chart processing
        const groupedUsers = safeUsers.reduce((acc, user) => {
          let dateStr = 'Unknown';
          if (user.createdAt?.seconds) {
            dateStr = new Date(user.createdAt.seconds * 1000).toLocaleDateString();
          } else if (user.registrationDate) {
            dateStr = new Date(user.registrationDate).toLocaleDateString();
          }
          acc[dateStr] = (acc[dateStr] || 0) + 1;
          return acc;
        }, {});

        const growthSeries = Object.keys(groupedUsers).map(key => ({
          label: key, value: groupedUsers[key]
        })).slice(-10);

        const feedbackStats = [
          { label: 'Positive', value: feeds.filter(f => (f.rating || 5) >= 4).length, color: '#2ecc71' },
          { label: 'Neutral', value: feeds.filter(f => (f.rating || 5) === 3).length, color: '#f1c40f' },
          { label: 'Negative', value: feeds.filter(f => (f.rating || 5) < 3).length, color: '#e74c3c' },
        ].filter(d => d.value > 0);

        setChartData({
          growth: growthSeries.length > 0 ? growthSeries : [{ label: 'Start', value: 0 }, { label: 'Now', value: safeUsers.length }],
          feedbackType: feedbackStats.length > 0 ? feedbackStats : [{ label: 'General', value: feeds.length, color: '#3b82f6' }],
        });

        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    intervalId = setInterval(fetchData, 30000); // Poll every 30s (was 10s — less aggressive)
    return () => clearInterval(intervalId);
  }, [navigate, isAdminAuthenticated, logout]);

  // ── Account unlock handler ──────────────────────────────────────────────────
  const handleUnlockAccount = async (email) => {
    try {
      const adminToken = SecureStorage.getToken(true);
      const res = await fetch(`${API_BASE_URL}/api/admin/unlock/${encodeURIComponent(email)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` },
      });
      const data = await res.json();
      if (res.ok) {
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ ${data.error}`);
      }
    } catch {
      alert('Could not unlock account. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Initializing Admin Nexus...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>System Alert</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reconnect</button>
      </div>
    );
  }

  // Group security logs by event type for quick stats
  const failedLogins = securityLogs.filter(l => l.eventType === 'failed_login').length;
  const lockedAccounts = securityLogs.filter(l => l.eventType === 'account_locked').length;

  return (
    <div className="admin-dashboard-container">
      <header className="admin-header">
        <div className="header-title">
          <h1>Admin Nexus</h1>
          <span className="live-indicator">● System Live</span>
        </div>
        <div className="admin-profile">
          <span>Administrator</span>
          <div className="admin-avatar">A</div>
          <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
        </div>
      </header>

      {/* ── Tab Navigation ── */}
      <nav className="dashboard-tabs">
        {Object.entries(TABS).map(([key, val]) => (
          <button
            key={key}
            className={`tab-btn ${activeTab === val ? 'active' : ''}`}
            onClick={() => setActiveTab(val)}
          >
            {key === 'OVERVIEW' && '📊 '}
            {key === 'USERS' && '👥 '}
            {key === 'FEEDBACK' && '💬 '}
            {key === 'SECURITY' && '🔒 '}
            {val.charAt(0).toUpperCase() + val.slice(1)}
            {key === 'SECURITY' && failedLogins > 0 && (
              <span className="security-badge-count">{failedLogins}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === TABS.OVERVIEW && (
        <>
          <section className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-icon users">👥</div>
              <div className="kpi-content">
                <h3>Total Users</h3>
                <p className="kpi-value">{userRegistrations.length}</p>
                <span className="kpi-trend positive">Active accounts</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon feedback">💬</div>
              <div className="kpi-content">
                <h3>Total Feedback</h3>
                <p className="kpi-value">{feedbacks.length}</p>
                <span className="kpi-trend neutral">Steady</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon system">🔒</div>
              <div className="kpi-content">
                <h3>Security Events</h3>
                <p className="kpi-value">{failedLogins}</p>
                <span className={`kpi-trend ${failedLogins > 10 ? 'negative' : 'positive'}`}>
                  {lockedAccounts} account{lockedAccounts !== 1 ? 's' : ''} locked
                </span>
              </div>
            </div>
          </section>

          <section className="analytics-grid">
            <div className="analytics-card wide">
              <LineChart title="User Registration Growth" data={chartData.growth} color="#FF6B00" />
            </div>
            <div className="analytics-card">
              <DonutChart title="Feedback Sentiment" data={chartData.feedbackType} />
            </div>
          </section>
        </>
      )}

      {/* ── USERS TAB ── */}
      {activeTab === TABS.USERS && (
        <div className="data-tables-grid">
          <section className="data-section" style={{ gridColumn: '1 / -1' }}>
            <div className="section-header">
              <h2>Registered Users</h2>
              <span className="record-count">{userRegistrations.length} total</span>
            </div>
            <div className="premium-table-wrapper">
              {userRegistrations.length > 0 ? (
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Company</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userRegistrations.map(user => (
                      <tr key={user.id}>
                        <td className="mono-font">#{String(user.id).slice(-6)}</td>
                        <td>{user.name || 'N/A'}</td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{user.phone || '—'}</td>
                        <td>{user.company || '—'}</td>
                        <td>
                          {user.createdAt?.seconds
                            ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                            : 'Recent'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty-state">No user registrations found.</p>
              )}
            </div>
          </section>
        </div>
      )}

      {/* ── FEEDBACK TAB ── */}
      {activeTab === TABS.FEEDBACK && (
        <div className="data-tables-grid">
          <section className="data-section" style={{ gridColumn: '1 / -1' }}>
            <div className="section-header">
              <h2>Latest Feedback</h2>
              <span className="record-count">{feedbacks.length} entries</span>
            </div>
            <div className="feedback-cards-scroller">
              {feedbacks.length > 0 ? feedbacks.map(feedback => (
                <div key={feedback.id} className="feedback-mini-card">
                  <div className="feedback-mini-header">
                    <span className="feedback-user">{feedback.name || 'Anonymous'}</span>
                    <span className="feedback-date">
                      {feedback.createdAt?.seconds
                        ? new Date(feedback.createdAt.seconds * 1000).toLocaleDateString()
                        : 'Just now'}
                    </span>
                  </div>
                  <p className="feedback-msg">"{feedback.comment || 'No comment'}"</p>
                  <div className={`feedback-rating rating-${feedback.overall || 3}`}>
                    {'★'.repeat(feedback.overall || 0)}
                  </div>
                </div>
              )) : (
                <p className="empty-state">No feedback entries found.</p>
              )}
            </div>
          </section>
        </div>
      )}

      {/* ── SECURITY TAB ── */}
      {activeTab === TABS.SECURITY && (
        <div className="data-tables-grid">
          <section className="data-section" style={{ gridColumn: '1 / -1' }}>
            <div className="section-header">
              <h2>🔒 Security Logs</h2>
              <span className="record-count">{securityLogs.length} recent events</span>
            </div>

            {/* Security Summary Cards */}
            <div className="security-summary">
              <div className="sec-stat-card">
                <span className="sec-stat-icon">⚠️</span>
                <div>
                  <p className="sec-stat-value">{failedLogins}</p>
                  <p className="sec-stat-label">Failed Logins</p>
                </div>
              </div>
              <div className="sec-stat-card">
                <span className="sec-stat-icon">🔐</span>
                <div>
                  <p className="sec-stat-value">{lockedAccounts}</p>
                  <p className="sec-stat-label">Accounts Locked</p>
                </div>
              </div>
              <div className="sec-stat-card">
                <span className="sec-stat-icon">🔑</span>
                <div>
                  <p className="sec-stat-value">
                    {securityLogs.filter(l => l.eventType === 'password_reset_attempt').length}
                  </p>
                  <p className="sec-stat-label">Reset Attempts</p>
                </div>
              </div>
            </div>

            <div className="premium-table-wrapper">
              {securityLogs.length > 0 ? (
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Email</th>
                      <th>IP Address</th>
                      <th>Time</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {securityLogs.map(log => (
                      <tr key={log.id} className={log.eventType === 'account_locked' ? 'row-danger' : ''}>
                        <td>
                          <span className={`event-badge event-${log.eventType}`}>
                            {log.eventType === 'failed_login' && '⚠️ Failed Login'}
                            {log.eventType === 'account_locked' && '🔐 Account Locked'}
                            {log.eventType === 'password_reset_attempt' && '🔑 Reset Attempt'}
                            {!['failed_login', 'account_locked', 'password_reset_attempt'].includes(log.eventType) && log.eventType}
                          </span>
                        </td>
                        <td>{log.email || '—'}</td>
                        <td className="mono-font">{log.ip || '—'}</td>
                        <td>
                          {log.timestamp?.seconds
                            ? new Date(log.timestamp.seconds * 1000).toLocaleString()
                            : 'Recent'}
                        </td>
                        <td>
                          {log.eventType === 'account_locked' && (
                            <button
                              className="unlock-btn"
                              onClick={() => handleUnlockAccount(log.email)}
                            >
                              Unlock
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty-state">No security events recorded yet. This is a good sign! ✅</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
